import {
  BillingOverviewReply,
  ListPricesReply,
  ListSubscriptionsReply,
  Price,
  PriceTier,
  SubscriptionLineItem,
  SubscriptionLite
} from '../../proto/billing-manager_pb';

// Lib (ported from marketing site) expects plain-shape objects without jspb's
// `*List` suffix on repeated fields. These adapters normalize at the boundary.

export type LibSubscriptionLineItem = {
  subscriptionItemId: string;
  priceId: string;
  productId: string;
  productName: string;
  quantity: number;
  unitAmount: number;
  priceNickname: string;
  dedicatedNickName: string;
};

export type LibSubscription = {
  id: string;
  // The Stripe subscription id (sub_xxx), which cancel offers name a plan by; `id` is billing-manager's.
  stripeId: string;
  currency: string;
  status: string;
  interval: string;
  productName: string;
  quantity: number;
  currentPaidTotal: number;
  unitAmount: number;
  createdAt?: { seconds: number; nanos: number };
  endsOn?: { seconds: number; nanos: number };
  dedicatedNickName: string;
  currentPeriodEnd: number;
  priceNickname: string;
  intervalCount: number;
  lineItems: LibSubscriptionLineItem[];
};

// priceCatalog.js (ported verbatim from marketing) reads snake_case fields
// (`unit_amount`, `up_to`, `flat_amount`, `trial_period_days`) because marketing's
// BFF returns Stripe-shaped JSON. jspb's `.AsObject` emits camelCase, so the
// adapter normalizes to snake_case at this boundary. camelCase aliases are kept
// for any code that already uses them.
export type LibPriceTier = {
  upTo: number | null;
  up_to: number | null;
  unitAmount: number;
  unit_amount: number;
  flatAmount: number;
  flat_amount: number;
};

export type LibPrice = {
  id: string;
  // Stripe price ID (price_xxx). Distinct from `id` (internal UUID). Used to
  // map sub line item priceIds back to catalog entries for variant detection.
  stripePriceId: string;
  name: string;
  unitAmount: number;
  unit_amount: number;
  nickname: string;
  trialPeriodDays: number;
  trial_period_days: number;
  billingScheme: string;
  billing_scheme: string;
  tiersMode: string;
  tiers_mode: string;
  tiers: LibPriceTier[];
  startingFromAmount: number;
  // Internal product UUID. Matches BillingOverview.trialUsedProductIds and sub
  // line item productId so per-product trial state can be resolved from a price.
  productId: string;
  product_id: string;
};

export type LibBillingOverview = {
  activeSubscription: string;
  accountLimit: number;
  addOns: string[];
  defaultPayment: unknown;
  joinDate?: { seconds: number; nanos: number };
  availableCredit: number;
  // Product IDs the user has already consumed their one-time trial on. Drives
  // trial-choice visibility, preview total, and checkout routing.
  trialUsedProductIds: string[];
  // Stripe account (legacy|cy) the user's customer lives in. The publishable
  // key itself is NOT sent by the backend: lib/stripe/userStripe maps this name
  // onto a build-time key (config.stripe_pk / config.stripe_pk_cy). Undefined on
  // older backends → treated as the legacy account.
  stripeAccount?: string;
};

function lineItemFromPb(li: SubscriptionLineItem.AsObject): LibSubscriptionLineItem {
  return {
    subscriptionItemId: li.subscriptionItemId,
    priceId: li.priceId,
    productId: li.productId,
    productName: li.productName,
    quantity: Number(li.quantity) || 0,
    unitAmount: Number(li.unitAmount) || 0,
    priceNickname: li.priceNickname,
    dedicatedNickName: li.dedicatedNickName
  };
}

export function subscriptionFromPb(s: SubscriptionLite.AsObject): LibSubscription {
  return {
    id: s.id,
    stripeId: s.stripeId,
    currency: s.currency,
    status: s.status,
    interval: s.interval,
    productName: s.productName,
    quantity: Number(s.quantity) || 0,
    currentPaidTotal: Number(s.currentPaidTotal) || 0,
    unitAmount: Number(s.unitAmount) || 0,
    createdAt: s.createdAt,
    endsOn: s.endsOn,
    dedicatedNickName: s.dedicatedNickName,
    currentPeriodEnd: Number(s.currentPeriodEnd) || 0,
    priceNickname: s.priceNickname,
    intervalCount: Number(s.intervalCount) || 0,
    lineItems: (s.lineItemsList || []).map(lineItemFromPb)
  };
}

export function subscriptionsFromPb(reply: ListSubscriptionsReply.AsObject | null | undefined): LibSubscription[] {
  return (reply?.subscriptionsList || []).map(subscriptionFromPb);
}

function tierFromPb(t: PriceTier.AsObject): LibPriceTier {
  const upTo = t.upTo?.value ?? null;
  const unitAmount = Number(t.unitAmount) || 0;
  const flatAmount = Number(t.flatAmount) || 0;
  return {
    upTo,
    up_to: upTo,
    unitAmount,
    unit_amount: unitAmount,
    flatAmount,
    flat_amount: flatAmount
  };
}

export function priceFromPb(p: Price.AsObject): LibPrice {
  const unitAmount = Number(p.unitAmount) || 0;
  const trialPeriodDays = Number(p.trialPeriodDays) || 0;
  return {
    id: p.id,
    stripePriceId: p.stripePriceId || '',
    name: p.name,
    unitAmount,
    unit_amount: unitAmount,
    nickname: p.nickname,
    trialPeriodDays,
    trial_period_days: trialPeriodDays,
    billingScheme: p.billingScheme,
    billing_scheme: p.billingScheme,
    tiersMode: p.tiersMode,
    tiers_mode: p.tiersMode,
    tiers: (p.tiersList || []).map(tierFromPb),
    startingFromAmount: Number(p.startingFromAmount) || 0,
    productId: p.productId || '',
    product_id: p.productId || ''
  };
}

export function pricesFromPb(reply: ListPricesReply.AsObject | null | undefined): { prices: LibPrice[] } {
  return { prices: (reply?.pricesList || []).map(priceFromPb) };
}

export function overviewFromPb(reply: BillingOverviewReply.AsObject | null | undefined): LibBillingOverview | null {
  if (!reply) return null;
  return {
    activeSubscription: reply.activeSubscription,
    accountLimit: Number(reply.accountLimit) || 0,
    addOns: reply.addOnsList || [],
    defaultPayment: reply.defaultPayment,
    joinDate: reply.joinDate,
    availableCredit: Number(reply.availableCredit) || 0,
    trialUsedProductIds: reply.trialUsedProductIdsList || [],
    stripeAccount: reply.stripeAccount || undefined
  };
}
